import React, { useEffect, useState } from 'react';
import { apiFetch } from './api';

const QuickOrderBar = ({
  symbol, brokers, busy, error, message, riskPerTrade = 70000,
  entryPrice, setEntryPrice, stopPrice, setStopPrice, stopPickMode, onToggleStopPick, onRecord,
}) => {
  const activeBrokers = brokers.filter((broker) => broker.is_active);
  const [brokerId, setBrokerId] = useState('');
  // null = auto-calculated from risk/entry/stop below; a string once the
  // user types their own quantity (same override pattern as finviz2).
  const [quantityOverride, setQuantityOverride] = useState(null);
  // null = inferred automatically (mirrors finviz2/screener's
  // fillsNow = limitPrice >= candle.close): an entry at or below the
  // current price is assumed already fillable/filled; an entry above it is
  // a breakout-style order still waiting for price to reach it, so it
  // starts unfilled. A manual tap overrides that guess either way.
  const [filledOverride, setFilledOverride] = useState(null);
  // Last close for the selected stock, used as a stand-in entry price so
  // typing just a stop loss is enough to see a suggested quantity - the
  // Entry field itself stays untouched (still required to submit).
  const [currentPrice, setCurrentPrice] = useState(null);

  useEffect(() => {
    setCurrentPrice(null);
    if (!symbol) return undefined;
    const controller = new AbortController();
    apiFetch(`/candles/${encodeURIComponent(symbol)}/daily`, { signal: controller.signal })
      .then((r) => r.json())
      .then((data) => {
        const candles = Array.isArray(data.candles) ? data.candles : [];
        const last = candles[candles.length - 1];
        if (last && Number(last.close) > 0) setCurrentPrice(Number(last.close));
      })
      .catch(() => {});
    return () => controller.abort();
  }, [symbol]);

  const suggestedQuantity = (() => {
    const stop = Number(stopPrice);
    const risk = Number(riskPerTrade);
    if (!(stop > 0) || !(risk > 0)) return '1';
    // Prefer what's actually typed, but while it's mid-typed and not yet a
    // usable value (e.g. "6" of "68", momentarily below the stop), fall
    // back to the current-price estimate instead of dropping to 1 - typing
    // the entry price shouldn't visibly reset the quantity partway through.
    const typedEntry = Number(entryPrice);
    const entry = typedEntry > stop ? typedEntry : (currentPrice > stop ? currentPrice : null);
    if (!(entry > stop)) return '1';
    const calculated = Math.floor(risk / (entry - stop));
    return calculated > 0 ? String(calculated) : '1';
  })();
  const quantity = quantityOverride === null ? suggestedQuantity : quantityOverride;

  const autoFilled = Number(entryPrice) > 0 && currentPrice != null
    ? Number(entryPrice) <= currentPrice
    : true;
  const filled = filledOverride === null ? autoFilled : filledOverride;

  useEffect(() => {
    if (activeBrokers.some((broker) => String(broker.id) === String(brokerId))) return;
    setBrokerId(activeBrokers[0] ? String(activeBrokers[0].id) : '');
  }, [brokers, brokerId, activeBrokers]);

  const submit = async (event) => {
    event.preventDefault();
    const saved = await onRecord({ symbol, brokerId, quantity, entryPrice, stopPrice, filled });
    if (saved) {
      setQuantityOverride(null);
      setEntryPrice('');
      setStopPrice('');
      setFilledOverride(null);
    }
  };

  return (
    <form className="quick-order-bar" onSubmit={submit}>
      <strong className="quick-order-symbol">{symbol || 'Select a stock'}</strong>
      <label><span>Broker</span>
        <select value={brokerId} onChange={(event) => setBrokerId(event.target.value)}>
          {activeBrokers.map((broker) => <option key={broker.id} value={broker.id}>{broker.name}</option>)}
        </select>
      </label>
      <label><span title="Suggested from risk per trade, entry and stop; you can amend it">Qty (₹{Number(riskPerTrade || 0).toLocaleString('en-IN')} risk)</span>
        <input aria-label="Quantity" placeholder="Qty" type="number" min="1" step="1" value={quantity} onChange={(event) => setQuantityOverride(event.target.value)} />
      </label>
      <label><span>Entry price</span>
        <input aria-label="Entry price" placeholder="Entry" type="number" min="0.01" step="0.01" value={entryPrice} onChange={(event) => setEntryPrice(event.target.value)} />
      </label>
      <label><span>Stop loss <small>optional</small></span>
        <div className="quick-stop-field">
          <input aria-label="Stop loss" placeholder="Stop" type="number" min="0.01" step="0.01" value={stopPrice} onChange={(event) => setStopPrice(event.target.value)} />
          <button type="button" className={stopPickMode ? 'picking' : ''} onClick={onToggleStopPick}>{stopPickMode ? 'Click chart' : 'Pick'}</button>
        </div>
      </label>
      <button
        type="button"
        className={`fill-toggle ${filled ? 'is-filled' : 'is-unfilled'}`}
        onClick={() => setFilledOverride(!filled)}
        title={filled ? 'Already filled by the broker - tap to mark as not yet filled' : "Not yet filled - sits under Orders until you tap this back to Filled"}
      >
        {filled ? 'Filled' : 'Unfilled'}
      </button>
      <button type="submit" disabled={busy || !symbol || !brokerId || !(Number(quantity) > 0) || !(Number(entryPrice) > 0)}>
        {busy ? 'Saving…' : 'Buy'}
      </button>
      {message && <span className="quick-order-message">{message}</span>}
      {error && <span className="quick-order-error">{error}</span>}
    </form>
  );
};

export default QuickOrderBar;
